/**
 * Read adapters for `quote-list` (plots plan S; contract in `../contract.ts`,
 * stored shapes in docs/deck-design-system/plots/read-adapters.md).
 *
 * Shape only: fields are renamed and copied, never computed, split or
 * re-joined. Stored shapes:
 *
 * - a bar slide's `quoteCard` with a stamped `plot`: read as stamped;
 * - a bar slide's `quoteCard` without one (decks built before S): one quote,
 *   the stored `attribution` copied to `speaker.name` as stored (never split
 *   into a name and a role); the stored title/stat header stays slide chrome
 *   (`SlideQuoteCard`);
 * - a summary `quotes` card with a stamped `plot`: read as stamped;
 * - a summary `quotes` card without one (every analysis built before S, and
 *   `legacySummaryCards.ts` featured quotes): LEGACY, today's `QuotesView`
 *   rules kept for this shape only. Initials as stored or derived from the
 *   name, and the host may join the image by name (`isLegacy`), which it never
 *   does for a stamped plot.
 */

import { QuoteListQuote, QuoteTextRun } from '@digital-twins/slide-schemas/generated/plot-types'

import { PlotReadAdapter, PlotReadAdapters, StoredPlotPayload } from '../contract'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

/** A stored run (analysis output): its `gloss` may be stored as null. */
export interface StoredQuoteTextRun {
  text: string
  gloss?: string | null
}

/** Stored runs as plot runs: a null gloss is an unglossed run. */
function plotTextRuns(runs: Array<StoredQuoteTextRun> | null | undefined): { textRuns?: Array<QuoteTextRun> } {
  if (!runs?.length) return {}
  return { textRuns: runs.map(({ text, gloss }) => (gloss ? { text, gloss } : { text })) }
}

function isQuoteListPlot(value: unknown): value is StoredPlotPayload<'quote-list'> {
  return (
    isRecord(value) &&
    value.kind === 'quote-list' &&
    isRecord(value.data) &&
    Array.isArray(value.data.quotes) &&
    isRecord(value.data.labels)
  )
}

// -- deck slide: a bar slide's quote card -------------------------------------------

/** The stored quoteCard fields these adapters read. */
export interface StoredSlideQuoteCard {
  title?: string | null
  stat?: string | null
  quote?: string | null
  /** `quote` glossed where it refers to a tested item by its shown label. */
  quoteRuns?: Array<StoredQuoteTextRun> | null
  attribution?: string | null
  plot?: unknown
}

export const slideQuoteCardStampedAdapter: PlotReadAdapter<'quote-list', StoredSlideQuoteCard> = {
  kind: 'quote-list',
  source: { surface: 'deck-slide', shape: 'quoteCard' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredSlideQuoteCard {
    return isRecord(stored) && isQuoteListPlot(stored.plot) && stored.plot.data.quotes.length > 0
  },
  read(stored: StoredSlideQuoteCard): StoredPlotPayload<'quote-list'> {
    // Python validated the plot against the quote-list schema before storing it.
    return stored.plot as StoredPlotPayload<'quote-list'>
  },
}

export const slideQuoteCardStoredAdapter: PlotReadAdapter<'quote-list', StoredSlideQuoteCard> = {
  kind: 'quote-list',
  source: { surface: 'deck-slide', shape: 'quoteCard' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredSlideQuoteCard {
    return isRecord(stored) && !isQuoteListPlot(stored.plot)
  },
  read(stored: StoredSlideQuoteCard): StoredPlotPayload<'quote-list'> {
    const name = stringOrNull(stored.attribution)
    return {
      kind: 'quote-list',
      data: {
        quotes: [
          {
            id: 'quote-card',
            text: stored.quote ?? '',
            ...plotTextRuns(stored.quoteRuns),
            speaker: name ? { name } : null,
          },
        ],
        labels: {},
      },
      props: { layout: 'card', size: 'slide' },
    }
  },
}

/** A bar slide's quote card as a quote-list payload (stamped first, else the stored read). */
export function readSlideQuoteCard(stored: StoredSlideQuoteCard): StoredPlotPayload<'quote-list'> {
  if (slideQuoteCardStampedAdapter.accepts(stored)) {
    return slideQuoteCardStampedAdapter.read(stored)
  }
  return slideQuoteCardStoredAdapter.read(stored)
}

// -- summary card: the quotes carousel ----------------------------------------------

/** One stored `SummaryQuote` (analysis output), the fields the legacy reader reads. */
export interface StoredSummaryQuote {
  text?: string | null
  respondentId?: string | null
  speakerName?: string | null
  initials?: string | null
  role?: string | null
  themeId?: string | null
  themeLabel?: string | null
  sentiment?: string | null
  questionId?: string | null
  questionText?: string | null
  assetId?: string | null
  textRuns?: Array<StoredQuoteTextRun> | null
}

/** The stored quotes payload fields these adapters read. */
export interface StoredQuotesCard {
  quotes?: Array<StoredSummaryQuote>
  plot?: unknown
}

/** Legacy initials: as stored, else the first letter of up to two words of the name. */
export function legacySpeakerInitials(name: string, stamped?: string | null): string {
  if (stamped) return stamped
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
}

const SENTIMENTS = new Set(['positive', 'neutral', 'negative', 'mixed'])

function legacyQuote(quote: StoredSummaryQuote, index: number): QuoteListQuote {
  const name = quote.speakerName ?? ''
  const sentiment = quote.sentiment && SENTIMENTS.has(quote.sentiment) ? quote.sentiment : null
  return {
    id: `quote-${index}`,
    text: quote.text ?? '',
    ...plotTextRuns(quote.textRuns),
    speaker: name
      ? {
          respondentId: quote.respondentId ?? null,
          name,
          initials: legacySpeakerInitials(name, quote.initials),
          role: quote.role ?? null,
        }
      : null,
    questionId: quote.questionId ?? null,
    questionText: quote.questionText ?? null,
    themeId: quote.themeId ?? null,
    themeLabel: quote.themeLabel ?? null,
    assetId: quote.assetId ?? null,
    sentiment: sentiment as QuoteListQuote['sentiment'],
  }
}

export const quotesCardStampedAdapter: PlotReadAdapter<'quote-list', StoredQuotesCard> = {
  kind: 'quote-list',
  source: { surface: 'summary-card', shape: 'quotes' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredQuotesCard {
    return isRecord(stored) && isQuoteListPlot(stored.plot) && stored.plot.data.quotes.length > 0
  },
  read(stored: StoredQuotesCard): StoredPlotPayload<'quote-list'> {
    // Python validated the plot against the quote-list schema before storing it.
    return stored.plot as StoredPlotPayload<'quote-list'>
  },
}

export const quotesCardLegacyAdapter: PlotReadAdapter<'quote-list', StoredQuotesCard> = {
  kind: 'quote-list',
  source: { surface: 'summary-card', shape: 'quotes' },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredQuotesCard {
    return isRecord(stored) && Array.isArray(stored.quotes) && !isQuoteListPlot(stored.plot)
  },
  read(stored: StoredQuotesCard): StoredPlotPayload<'quote-list'> {
    return {
      kind: 'quote-list',
      data: { quotes: (stored.quotes ?? []).map(legacyQuote), labels: {} },
      props: { layout: 'carousel', size: 'card' },
    }
  },
}

export interface ReadQuotesCard {
  plot: StoredPlotPayload<'quote-list'>
  /** True for a card stored before S: the host may join images by name. */
  isLegacy: boolean
}

/** A summary quotes card as a quote-list payload (stamped first, else the legacy read). */
export function readQuotesCard(stored: StoredQuotesCard): ReadQuotesCard {
  if (quotesCardStampedAdapter.accepts(stored)) {
    return { plot: quotesCardStampedAdapter.read(stored), isLegacy: false }
  }
  return { plot: quotesCardLegacyAdapter.read(stored), isLegacy: true }
}

export const QUOTE_LIST_READ_ADAPTERS: PlotReadAdapters<'quote-list'> = [
  slideQuoteCardStampedAdapter,
  slideQuoteCardStoredAdapter,
  quotesCardStampedAdapter,
  quotesCardLegacyAdapter,
]
